import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface TwoFactorStatus {
  enabled: boolean;
  setupAt?: string;
}

export interface TwoFactorSetupResponse {
  qrCodeUrl: string;
  backupCodes: string[];
  message: string;
}

export interface TwoFactorResponse {
  success: boolean;
  message: string;
  backupCodes?: string[];
}

class TwoFactorService {
  private async makeRequest(url: string, options: RequestInit = {}): Promise<any> {
    const token = authService.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Get current 2FA status
  async getStatus(): Promise<TwoFactorStatus> {
    return this.makeRequest(`${API_BASE_URL}/admin/2fa/status`);
  }

  // Start 2FA setup - generates QR code and backup codes
  async startSetup(): Promise<TwoFactorSetupResponse> {
    return this.makeRequest(`${API_BASE_URL}/admin/2fa/setup`, {
      method: 'POST',
    });
  }

  // Verify setup with TOTP code
  async verifySetup(code: string): Promise<TwoFactorResponse> {
    return this.makeRequest(`${API_BASE_URL}/admin/2fa/verify-setup`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // Disable 2FA
  async disable(password: string, code: string): Promise<TwoFactorResponse> {
    return this.makeRequest(`${API_BASE_URL}/admin/2fa/disable`, {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    });
  }

  // Regenerate backup codes
  async regenerateBackupCodes(code: string): Promise<TwoFactorResponse> {
    return this.makeRequest(`${API_BASE_URL}/admin/2fa/regenerate-backup-codes`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }
}

export const twoFactorService = new TwoFactorService();
export default twoFactorService;